import { AfterContentInit, Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { SessionItemRow } from '../../providers/sessionItems-provider';
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { SessionsProvider } from '../../providers/sessions-provider';
import { HasPendingChangesGuard } from '../../../common/guards/has-pending-changes';
import { EditableBlockInterface } from '../../editable-block-interface';
import { SessionExerciceRepeatEditComponent } from './session-exercice-repeat-edit';
import { Organization } from "../../../common/models/organization";
import { NgForOf, NgIf } from "@angular/common";
import { MatIconButton } from "@angular/material/button";
import {
  ExerciceCategoryThumbnailComponent
} from "../../../exercices/components/exercice-category-thumbnail/exercice-category-thumbnail";
import { ExercicesVideoButtonComponent } from "../../../exercices/components/video-button/video-button";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { MatTooltip } from "@angular/material/tooltip";
import { FlexLayoutModule } from "@angular/flex-layout";
import { ShareComponent } from "../../../chat/share/share.component";
import { MatIcon } from "@angular/material/icon";
import { TranslateModule } from "@ngx-translate/core";
import { DurationVerbosePipe } from "../../../common/pipes/duration-verbose";
import { Exercice } from "../../../common/models/exercice";
import { ConfigProvider } from "../../../config-provider.service";

@Component({
  selector: 'session-exercice-repeat',
  providers: [],
  styleUrls: ['../common/session-exercice/session-exercice.scss', './session-exercice-repeat.scss'],
  standalone: true,
  imports: [
    NgForOf,
    ExercicesVideoButtonComponent,
    ExerciceCategoryThumbnailComponent,
    MatIconButton,
    NgIf,
    MatMenuTrigger,
    MatMenuItem,
    MatTooltip,
    FlexLayoutModule,
    ShareComponent,
    MatIcon,
    TranslateModule,
    DurationVerbosePipe,
    MatMenu
  ],
  templateUrl: './session-exercice-repeat.html'
})
export class SessionExerciceRepeatComponent implements AfterContentInit, EditableBlockInterface {
    @Input() public isMobileDisplay = false;
    @Input() public basicDisplay = false;
    @Input() public organization: Organization | null = null;
    @Input() public data: SessionItemRow = {} as SessionItemRow;
    @Input() public sessionId: number | undefined
    @Input() public isEditable = true;
    @Input() public itsTheFirst = false;
    @Input() public itsTheLast = false;
    @Input() public parentIndex: number | undefined;
    public _duration: string= '';
    public _restTime: string= '';
    public _totalPayload: number |null = null;
    public _totalTime: number |null = null;
    @Output('onTimeBasedInfoUpdated') public onTimeBasedInfoUpdated: EventEmitter<any[]> = new EventEmitter<any[]>();
    @Output('onDelete') public onDelete: EventEmitter<SessionItemRow> = new EventEmitter<SessionItemRow>();
    @Output('onPersonalizedExerciceClicked') public onPersonalizedExerciceClicked: EventEmitter<SessionItemRow> = new EventEmitter<SessionItemRow>();
    @Output('onDuplicate') public onDuplicate: EventEmitter<SessionItemRow> = new EventEmitter<SessionItemRow>();
    @Output('onMoveUp') public onMoveUp: EventEmitter<SessionItemRow> = new EventEmitter<SessionItemRow>();
    @Output('onMoveDown') public onMoveDown: EventEmitter<SessionItemRow> = new EventEmitter<SessionItemRow>();
    @Output() public onChanged: EventEmitter<any> = new EventEmitter<any>();
    @Output('onDuplicateAsModel') public onDuplicateAsModel: EventEmitter<SessionItemRow> = new EventEmitter<SessionItemRow>();
    @Output() public onValuesAreGood: EventEmitter<SessionItemRow> = new EventEmitter<SessionItemRow>();
    @Output() public onValuesAreNotGood: EventEmitter<SessionItemRow> = new EventEmitter<SessionItemRow>();

    public isSelected = false;

    constructor(
      public sessionsProvider: SessionsProvider,
      private _utils: UtilsProvider,
      private _changesGuard: HasPendingChangesGuard,
      protected conf: ConfigProvider,
    ) {
    }

    public editComponent(): any {
        return SessionExerciceRepeatEditComponent;
    }

    public toggleSelected(bool: boolean): void {
        this.isSelected = bool;
    }

    @HostListener('window:beforeunload')
    public doSomething() {
        return !this._changesGuard.hasChanges();
    }

    public ngAfterContentInit() {
        this.setDefaultValues();
        this.setFormattedDuration();
        this.calcTotalPayload();
        this.calcTotalTime();
    }

    // define this.data.data.exercice.exercice as Exercice to avoid error in the template
    public getAsSharableType(data: any) {
        return new Exercice({
          id: data.id,
          name: data.name,
        })
    }

    public duplicate(toDuplicate: SessionItemRow): void {
        // set a default id other than "0" to avoid value reset. See : this.setDefaultValues()
        this.data.data.sessionItemExercice.id = -1;

        this.onDuplicate.emit(toDuplicate);
        this.onChanged.emit();
    }

    public personalized(toPersonalized: SessionItemRow): void {
        this.onPersonalizedExerciceClicked.emit(toPersonalized);
        this.onChanged.emit();
    }

    public delete(toDelete: SessionItemRow): void {
        this.onDelete.emit(toDelete);
        this.onChanged.emit();
    }

    public calcTotalPayload() {
        this._totalPayload =
            this.data.data.sessionItemExercice.payload  *
            this.data.data.sessionItemExercice.nbRepeat *
            this.data.data.sessionItemExercice.nbSerie;
    }

    public calcTotalTime() {
        this._totalTime = (
            this.data.data.sessionItemExercice.duration *
                this.data.data.sessionItemExercice.nbRepeat *
                this.data.data.sessionItemExercice.nbSerie
        )                                               +
            (
                this.data.data.sessionItemExercice.nbSerie  *
                this.data.data.sessionItemExercice.restTime
            );
    }

    public setFormattedDuration() {
        this._duration = this.data.data.sessionItemExercice.duration;
        this._restTime = this.data.data.sessionItemExercice.restTime;
    }

    public moveup(toMove: SessionItemRow): void {
        toMove.parentIndex = this.parentIndex ?? 0;
        this.onMoveUp.emit(toMove);
    }

    public movedown(toMove: SessionItemRow): void {
        toMove.parentIndex = this.parentIndex ?? 0;
        this.onMoveDown.emit(toMove);
    }

    public duplicateAsModel(toDuplicate: SessionItemRow) {
        this.onDuplicateAsModel.emit(toDuplicate);
    }

    private setDefaultValues() {
        if (this.data.data.sessionItemExercice.id === 0) {
            this.data.data.sessionItemExercice.duration = this.data.data.exercice.exercice.duration;
            this.data.data.sessionItemExercice.nbRepeat = 1;
            this.data.data.sessionItemExercice.nbSerie = 1;
            this.data.data.sessionItemExercice.payload = 0;
            this.data.data.sessionItemExercice.restTime = 0;

        }
    }
}
